<script setup>
import { ref, watchEffect, computed, reactive } from "vue";
import { useQuasar } from "quasar";
import { useRoute } from "vue-router";
import { fetchWithHeaders } from "@/utils";
import { toDate, toTime, toDuration, toISOString, sessionFormat } from "@/filters";
import { isRequired } from "@/rules";
import SlotWizardDialog from "@/components/SlotWizardDialog.vue";

const $q = useQuasar();
const route = useRoute();
const session = ref();
const slots = ref([]);
watchEffect(async () => {
    if (!route.params.sessionCode) return;
    const url = `${window._env_.API_ENDPOINT}/sessions/${route.params.sessionCode}/`;
    session.value = await fetchWithHeaders(url);
    loadSlots();
});

async function loadSlots() {
    const urlSlots = `${window._env_.API_ENDPOINT}/slots/?session=${route.params.sessionCode}`;
    slots.value = await fetchWithHeaders(urlSlots);
    selectedDate.value = session.value.open_date;
}

const proxyDate = ref(null);
function saveSessionDates() {
    if (!proxyDate.value) return;
    if (typeof proxyDate.value === "string") {
        session.value.open_date = proxyDate.value;
        session.value.close_date = proxyDate.value;
    } else {
        session.value.open_date = proxyDate.value.from;
        session.value.close_date = proxyDate.value.to;
    }
    saveSession();
}

function saveSession() {
    const url = `${window._env_.API_ENDPOINT}/sessions/${route.params.sessionCode}/`;
    fetchWithHeaders(url, {
        method: "PUT",
        body: JSON.stringify(session.value),
    });
}

/* slot */
// asuming sorted by api
const slotsGroupedByDate = computed(() => {
    return slots.value.reduce((groups, slot) => {
        const date = slot.start.split("T")[0];
        if (!groups[date]) {
            groups[date] = [];
        }
        groups[date].push(slot);
        return groups;
    }, {});
});
const events = computed(() => Object.keys(slotsGroupedByDate.value).map(d => d.replace(/-/g, "/")));

const selectedDate = ref(null);

function limitToDateRange(date) {
    return (
        events.value.includes(date) ||
        (date >= session.value.open_date.replace(/-/g, "/") && date <= session.value.close_date.replace(/-/g, "/"))
    );
}

const initialPagination = {
    page: 1,
    rowsPerPage: 0,
    sortBy: "start",
    descending: false,
};

const columns = [
    { name: "start", label: "Début", field: "start", align: "left", format: val => toTime(val) },
    { name: "end", label: "Fin", field: "end", align: "left", format: val => toTime(val) },
    { name: "duration", label: "Durée", align: "left" },
    { name: "room", label: "Salle", field: "room", align: "left" },
    {
        name: "seats",
        label: "Places",
        field: "seats",
        align: "left",
        format: (val, row) => `${row.registrations_count} / ${val}`,
    },
    { name: "action", label: "Action" },
];

const newSlot = reactive({
    start: "",
    end: "",
    room: "",
    seats: 0,
});
watchEffect(() => {
    if (session.value) {
        newSlot.room = session.value.default_room;
        newSlot.seats = session.value.default_seats;
    }
});
async function addSlot() {
    const copy = {};
    Object.assign(copy, newSlot);
    copy.start = toISOString(`${selectedDate.value} ${copy.start}`);
    copy.end = toISOString(`${selectedDate.value} ${copy.end}`);
    copy.session = session.value.code;
    const url = `${window._env_.API_ENDPOINT}/slots/`;
    const slot = await fetchWithHeaders(url, {
        method: "POST",
        body: JSON.stringify(copy),
    });
    slot.registrations_count = 0;
    slots.value.push(slot);
}

async function patchSlot(slot, patch) {
    const url = `${window._env_.API_ENDPOINT}/slots/${slot.pk}/`;
    await fetchWithHeaders(url, {
        method: "PATCH",
        body: JSON.stringify(patch),
    });
    Object.keys(patch).forEach(key => {
        slot[key] = patch[key];
    });
}

function removeSlot(slot) {
    $q.dialog({
        title: "Suppression?",
        message: `Supprimer le créneau de ${toTime(slot.start)} à ${toTime(slot.end)}`,
        cancel: true,
        persistent: true,
    }).onOk(async () => {
        const url = `${window._env_.API_ENDPOINT}/slots/${slot.pk}`;
        await fetchWithHeaders(url, {
            method: "DELETE",
        });
        slots.value.splice(slots.value.indexOf(slot), 1);
    });
}
function createSlots() {
    $q.dialog({
        component: SlotWizardDialog,
        componentProps: {
            session: session.value,
        },
    }).onOk(() => {
        loadSlots();
    });
}
</script>
<template>
    <main v-if="session">
        <q-card class="q-pa-md q-mt-lg q-mx-auto">
            <div class="q-mb-lg row q-col-gutter-lg">
                <div class="text-h4">Session {{ sessionFormat(session.code) }}</div>
                <div>
                    <q-toggle
                        v-model="session.state"
                        :label="session.state == 'C' ? 'Fermé' : 'Ouvert'"
                        checked-icon="fa-solid fa-lock-open"
                        unchecked-icon="fa-solid fa-lock"
                        false-value="C"
                        true-value="O"
                        keep-color
                        size="lg"
                        :color="session.state == 'C' ? 'negative' : 'positive'"
                        :class="session.state == 'C' ? 'text-negative' : 'text-positive'"
                        @update:model-value="saveSession()"
                    />
                </div>
                <q-space />
                <div>
                    <q-btn
                        label="ajouter créneaux"
                        color="primary"
                        outline=""
                        icon="fa-solid fa-plus"
                        @click="createSlots"
                    />
                </div>
            </div>
            <div class="row q-col-gutter-lg">
                <div>
                    <q-field label="Période d'ouverture" stack-label>
                        <template #control>
                            <div class="self-center no-outline" tabindex="0">
                                {{ toDate(session.open_date) }} - {{ toDate(session.close_date) }}
                            </div>
                        </template>
                        <template #append>
                            <q-btn icon="fa-solid fa-calendar-day" flat color="primary">
                                <q-popup-proxy
                                    transition-show="scale"
                                    transition-hide="scale"
                                    @before-show="proxyDate = { from: session.open_date, to: session.close_date }"
                                >
                                    <q-date v-model="proxyDate" mask="YYYY-MM-DD" range>
                                        <div class="row items-center justify-end q-gutter-sm">
                                            <q-btn v-close-popup label="Annuler" color="primary" flat />
                                            <q-btn
                                                v-close-popup
                                                label="OK"
                                                color="primary"
                                                flat
                                                @click="saveSessionDates"
                                            />
                                        </div>
                                    </q-date>
                                </q-popup-proxy>
                            </q-btn>
                        </template>
                    </q-field>
                </div>
                <div>
                    <q-input
                        v-model="session.default_seats"
                        type="number"
                        label="Place par défaut"
                        :rules="[isRequired]"
                        required
                        debounce="1000"
                        @update:model-value="saveSession()"
                    ></q-input>
                </div>
                <div>
                    <q-input
                        v-model="session.default_room"
                        type="text"
                        label="Salle par défaut"
                        :rules="[isRequired]"
                        required
                        @blur="saveSession()"
                    ></q-input>
                </div>
            </div>
            <div class="text-h4 q-my-lg">Créneaux</div>
            <div class="row">
                <div class="col-md-3 q-mb-lg">
                    <q-date
                        v-model="selectedDate"
                        :events="events"
                        mask="YYYY-MM-DD"
                        event-color="primary"
                        :options="limitToDateRange"
                    />
                </div>
                <div class="col-md-9">
                    <q-table
                        v-if="selectedDate"
                        row-key="pk"
                        :columns="columns"
                        :rows="slotsGroupedByDate[selectedDate] || []"
                        :pagination="initialPagination"
                        hide-bottom
                        square
                    >
                        <template #body="props">
                            <q-tr :props="props">
                                <q-td>
                                    <q-input
                                        :model-value="props.colsMap.start.format(props.row.start)"
                                        dense
                                        filled
                                        mask="##:##"
                                        stack-label
                                        placeholder="08:00"
                                        style="max-width: 80px"
                                        :disable="props.row.registrations_count > 0"
                                        debounce="1000"
                                        @update:model-value="
                                            patchSlot(props.row, { start: toISOString(`${selectedDate} ${$event}`) })
                                        "
                                    />
                                </q-td>
                                <q-td>
                                    <q-input
                                        :model-value="props.colsMap.end.format(props.row.end)"
                                        dense
                                        filled
                                        mask="##:##"
                                        stack-label
                                        placeholder="10:00"
                                        style="max-width: 80px"
                                        :disable="props.row.registrations_count > 0"
                                        debounce="1000"
                                        @update:model-value="
                                            patchSlot(props.row, { end: toISOString(`${selectedDate} ${$event}`) })
                                        "
                                    />
                                </q-td>
                                <q-td>
                                    {{ toDuration(props.row.start, props.row.end) }}
                                </q-td>
                                <q-td>
                                    <q-input
                                        v-model="props.row.room"
                                        dense
                                        filled
                                        placeholder="Salle"
                                        debounce="1000"
                                        @update:model-value="patchSlot(props.row, { room: $event })"
                                    />
                                </q-td>
                                <q-td class="text-right">
                                    <q-input
                                        v-model="props.row.seats"
                                        dense
                                        filled
                                        type="number"
                                        :min="props.row.registrations_count"
                                        style="max-width: 100px"
                                        debounce="1000"
                                        @update:model-value="patchSlot(props.row, { seats: $event })"
                                    >
                                        <template #before>
                                            <span class="text-body1">{{ props.row.registrations_count }} /</span>
                                        </template>
                                    </q-input>
                                </q-td>
                                <q-td class="text-right"
                                    ><q-btn
                                        icon="fa-solid fa-trash"
                                        round
                                        flat
                                        color="negative"
                                        size="sm"
                                        :disable="props.row.registrations_count > 0"
                                        @click="removeSlot(props.row)"
                                    ></q-btn>
                                </q-td>
                            </q-tr>
                        </template>
                        <template #bottom-row>
                            <q-tr>
                                <q-td>
                                    <q-input
                                        v-model="newSlot.start"
                                        dense
                                        mask="##:##"
                                        filled
                                        placeholder="08:00"
                                        style="max-width: 80px"
                                    />
                                </q-td>
                                <q-td>
                                    <q-input
                                        v-model="newSlot.end"
                                        dense
                                        mask="##:##"
                                        filled
                                        placeholder="10:00"
                                        style="max-width: 80px"
                                    />
                                </q-td>
                                <q-td>
                                    <div>
                                        {{
                                            toDuration(
                                                toISOString(`${selectedDate} ${newSlot.start}`),
                                                toISOString(`${selectedDate} ${newSlot.end}`)
                                            )
                                        }}
                                    </div>
                                </q-td>
                                <q-td>
                                    <q-input v-model="newSlot.room" dense filled />
                                </q-td>
                                <q-td class="text-right">
                                    <q-input
                                        v-model="newSlot.seats"
                                        dense
                                        filled
                                        type="number"
                                        min="0"
                                        style="max-width: 100px"
                                    />
                                </q-td>
                                <q-td class="text-right">
                                    <q-btn
                                        icon="fa-solid fa-plus"
                                        label="ajouter"
                                        unelevated
                                        color="primary"
                                        :disable="!newSlot.end || !newSlot.start"
                                        @click="addSlot"
                                    ></q-btn>
                                </q-td>
                            </q-tr>
                        </template>
                    </q-table>
                </div>
            </div>

            <div class="row q-mt-lg">
                <q-space />
                <q-btn color="primary" class="nav-button" label="Retour" :to="`/admin`" />
            </div>
        </q-card>
    </main>
</template>
